<template>
  <div class="flex items-center mt-2 ml-2">
    <button
      type="button"
      :aria-label="t('settings.decreaseFontSize')"
      :title="t('settings.decreaseFontSize')"
      :disabled="checkDisabled('decrease') ? true : false"
      class="square-selector flex items-center justify-center"
      :class="checkDisabled('decrease') ? 'button-disabled' : null"
      @click="handleReduceFont"
    >
      <span class="material-icons" aria-hidden="true"> text_decrease </span>
    </button>
    <input
      type="text"
      class="font-size"
      inputmode="numeric"
      pattern="[0-9]*"
      :value="displayValue"
      :aria-label="t('settings.selectFontSize')"
      @focus="onFocus"
      @input="handleInput"
      @blur="validateFontSize"
      @keydown.enter.prevent
    />
    <button
      type="button"
      :aria-label="t('settings.increaseFontSize')"
      :title="t('settings.increaseFontSize')"
      :disabled="checkDisabled('increase') ? true : false"
      class="square-selector flex items-center justify-center"
      :class="checkDisabled('increase') ? 'button-disabled' : null"
      @click="handleIncreaseFont"
    >
      <span class="material-icons" aria-hidden="true"> text_increase </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const fontSize = defineModel<number>({
  default: 1,
});

const displayValue = ref(`${(fontSize.value * 100).toFixed(0)}%`);

function handleReduceFont(e: Event): void {
  e.preventDefault();
  fontSize.value = +(fontSize.value - 0.05).toFixed(2);
}

function handleIncreaseFont(e: Event): void {
  e.preventDefault();
  fontSize.value = +(fontSize.value + 0.05).toFixed(2);
}

function validateFontSize() {
  const number = parseInt(displayValue.value, 10);
  if (isNaN(number)) {
    displayValue.value = `${Math.round(fontSize.value * 100)}%`;
    return;
  }
  fontSize.value = number / 100;
  if (number <= 70) fontSize.value = 0.7;
  if (number >= 250) fontSize.value = 2.5;
  displayValue.value = `${Math.round(fontSize.value * 100)}%`;
}

function handleInput(event: Event) {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  if (/^\d*$/.test(value)) {
    console.log("Valid input", /^\d*$/.test(value));
    displayValue.value = value;
  } else {
    console.log("inValid input", /^\d*$/.test(value));
    displayValue.value = "";
  }
}

function onFocus() {
  displayValue.value = "";
}

watch(
  fontSize,
  () => {
    displayValue.value = `${Math.round(fontSize.value * 100)}%`;
  },
  { immediate: true }
);

function checkDisabled(button: string): boolean {
  if (button === "decrease" && fontSize.value <= 0.7) return true;
  if (button === "increase" && fontSize.value >= 2.5) return true;
  return false;
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.square-selector {
  width: 3.5rem;
  height: 2.5rem;
  border-radius: $spacingXS;
  border: $spacingXS solid var(--DM__theme-hover-color);
  transition: all 0.1s ease;

  @media (any-hover: hover) {
    &:hover {
      border-color: var(--DM__theme-slider-color);
      background-color: var(--DM__theme-hover-color);
    }
  }

  &:focus {
    border-color: var(--DM__theme-border-color);
    outline: 0;
    &:not(:focus-visible) {
      border-color: var(--DM__theme-slider-color);
    }
  }

  &.button-disabled {
    color: var(--DM__color-grisFonce);
    opacity: 0.7;
    border-color: var(--DM__color-grisFonce);
    background-color: var(--DM__color-fill);
    cursor: not-allowed;
    @media (any-hover: hover) {
      &:hover {
        border-color: var(--DM__color-grisFonce);
        background-color: var(--DM__color-fill);
      }
    }
  }
}

input.font-size {
  width: 5rem;
  height: 2.5rem;
  text-align: center;
  font-size: 1.2rem;
  border: $spacingXS solid transparent;
  background-color: var(--DM__color-fill);
  color: var(--DM__theme-text-color);
  transition: all 0.1s ease;

  &:focus {
    border-color: var(--DM__theme-border-color);
    outline: 0;
    &:not(:focus-visible) {
      border-color: var(--DM__theme-slider-color);
    }
  }
}
</style>
